/**
 * The people a member can be linked to (docs/plans/oncall-line-plan.md 6.2,
 * 7.6 oncall_members.user_id, 7.7; WP-11).
 *
 * A member's optional link to a platform user is what lets that person use
 * "Call back" in SiteOps (plan 6.2: the bridge rings the requesting user's
 * linked member cell). The 24-Hour Line has no user directory route of its
 * own (plan 7.5 lists none), so the member form reads the site's users from
 * the platform's existing site roster, GET /api/siteorganizer/site/:siteId/
 * users (authenticated, the caller must have access to that site; it answers
 * [{ id, email, full_name }] for the users granted that site). The server
 * still judges every link: PATCH /members checks the id is a live platform
 * user and not linked to another member of the line (409
 * ONCALL_USER_ALREADY_LINKED).
 *
 * The roster is a convenience, not a dependency: when it cannot be read the
 * form still offers "Not linked", the signed-in admin themself, and whoever
 * the member is linked to now (plan 0.5: smaller, and nothing on the call
 * path depends on it). A module-owned route (IXVerify's /api/ixverify/
 * site-users is the precedent) would replace this read; WP-11's NOTES say so.
 */
import api from '../../../services/api';

export interface SiteUser {
  id: string;
  email: string;
  full_name: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The site's users, by name; rows without a usable id are dropped. Throws when the roster cannot be read. */
export async function listSiteUsers(siteId: string): Promise<SiteUser[]> {
  const response = await api.get<unknown>(`/api/siteorganizer/site/${encodeURIComponent(siteId)}/users`);
  const rows = Array.isArray(response.data) ? response.data : [];
  const seen = new Set<string>();
  const users: SiteUser[] = [];
  for (const row of rows as Array<Partial<SiteUser> | null>) {
    if (!row || typeof row.id !== 'string' || !UUID_RE.test(row.id) || seen.has(row.id)) continue;
    seen.add(row.id);
    users.push({
      id: row.id,
      email: typeof row.email === 'string' ? row.email : '',
      full_name: typeof row.full_name === 'string' ? row.full_name : '',
    });
  }
  return users.sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
}

/** "Full Name (email)", or whichever of the two exists. */
export function userLabel(user: Pick<SiteUser, 'full_name' | 'email'>): string {
  const name = user.full_name.trim();
  const email = user.email.trim();
  if (name && email) return `${name} (${email})`;
  return name || email || 'Unnamed user';
}
